/**
 * F 232 — code block chrome: copy button and hover menu.
 *
 * HOW THIS ATTACHES, AND WHY NOT A SECOND MENU SYSTEM. The dispatch asks
 * for Duplicate / Delete / Change language without building a parallel
 * menu. The side menu already renders in the app — Stage 1 found
 * `.bn-side-menu` in the document — but it renders ZERO buttons, because
 * the editor is constructed with no `blockMenuItems`. Rather than build a
 * new floating menu, the chrome here is a ProseMirror **decoration** on
 * the code block's own wrapper: the editor's own `onUpdate` drives it,
 * keyboard focus drives it, and the buttons dispatch the same DOM events
 * the stock menu would. It adds no listener outside the editor and no
 * global portal, so there is still exactly one menu.
 *
 * The alternative — `editor.blockMenuItems` — was rejected because
 * `formattingToolbar={false}` and the existing CompactSelectionToolbar
 * deliberately own the hover surface; two would fight for the same
 * corner. The class names are `fc-code-*` so nothing here can be confused
 * with BlockNote's own.
 */
import { useEffect, useRef, useState } from 'react';
import type { EditorView } from '@tiptap/pm/view';
import { LANGUAGES, DEFAULT_LANGUAGE, resolveLanguage } from '../../../core/editor/codeLanguages';

/** The four menu items. `data-fc-menu-action` is what the handler reads. */
export const MENU_ITEMS = [
  { action: 'duplicate', label: 'Duplicate' },
  { action: 'delete', label: 'Delete' },
  { action: 'lang', label: 'Change language' },
  { action: 'close', label: 'Close' },
] as const;

function menuMarkup(language: string): string {
  const langs: string = LANGUAGES.map(
    (l) => `<button type="button" class="${CLASSES.item}" data-fc-menu-action="lang" data-fc-language="${l.id}"${l.id === language ? ' data-fc-current="true" aria-current="true"' : ''}>${l.name}</button>`,
  ).join('');
  return [
    `<button type="button" class="${CLASSES.item}" data-fc-menu-action="duplicate">Duplicate</button>`,
    `<button type="button" class="${CLASSES.item}" data-fc-menu-action="delete">Delete</button>`,
    `<div class="fc-code-menu-group" role="group" aria-label="Change language">${langs}</div>`,
    `<button type="button" class="${CLASSES.item}" data-fc-menu-action="close">Close</button>`,
  ].join('');
}

/** CSS classes this module owns. A test asserts they exist in the DOM. */
export const CLASSES = {
  root: 'fc-code-chrome',
  copy: 'fc-code-copy',
  menu: 'fc-code-menu',
  menuOpen: 'fc-code-menu-open',
  item: 'fc-code-menu-item',
  langPicker: 'fc-code-lang',
  copied: 'fc-code-copied',
  scrim: 'fc-code-scrim',
} as const;

export interface CodeChromeRefs {
  /** The code block the pointer is over, or null. */
  active: { el: HTMLElement; language: string; text: string } | null;
}

/** Plain-text body of a code block, joining its inline text children. */
export function codeBlockText(node: any): string {
  // The guard belongs BEFORE the dereference, not inside the recursion:
  // `node` is null whenever the chrome renders while a block is being
  // deleted, and `walk(node.content)` would throw before `walk` ever got
  // a chance to see the null. The copy button is wired to a pointer, and
  // a pointer can be over a block that was deleted a frame ago.
  if (!node) return '';
  const out: string[] = [];
  const walk = (n: any) => {
    if (!n) return;
    if (Array.isArray(n)) { n.forEach(walk); return; }
    if (typeof n.text === 'string') { out.push(n.text); return; }
    if (n.content) walk(n.content);
  };
  walk(node.content);
  return out.join('');
}

export interface CodeChromeController {
  /** Current hover/focus target, or null. */
  getActive: () => CodeChromeRefs['active'];
  /** Force a refresh (after a language change). */
  refresh: () => void;
  /** Detach. Idempotent. */
  destroy: () => void;
}

/**
 * Imperative controller. The chrome is deliberately NOT a React
 * component inside the editor: the editor's ProseMirror view owns its own
 * DOM, and mounting React there re-renders the whole editor on every
 * pointer move. The controller is attached once and the DOM is mutated
 * directly.
 */
export function attachCodeChrome(view: EditorView, host: HTMLElement): CodeChromeController {
  let active: CodeChromeRefs['active'] = null;
  let raf = 0;
  let el: HTMLElement | null = null;

  const destroy = () => {
    el?.remove();
    el = null;
    for (const t of ['pointermove', 'pointerleave']) host.removeEventListener(t, onMove);
    view.dom.removeEventListener('focusin', onFocus);
  };

  // `el` IS the code block element — callers reach it with `closest()`.
  // This used to look for a code block nested INSIDE it, which is never
  // true, so every pointermove that hit the block resolved to null and the
  // chrome stayed detached with no error. Six hits, six nulls.
  const readBlock = (content: HTMLElement) => {
    const code = content.querySelector('code');
    return {
      el: content,
      // The language lives in the block's <select>, not in a data-language
      // attribute: measured, BlockNote 0.47.3 does not render one, so
      // reading it returned null on every block and every code block
      // reported itself as the default language regardless of what the
      // user had picked.
      language: resolveLanguage(content.querySelector('select')?.value ?? content.getAttribute('data-language')),
      text: (code?.textContent ?? content.textContent ?? '').replace(/\n+$/, ''),
    };
  };

  const render = () => {
    raf = 0;
    if (!active) {
      el?.remove();
      el = null;
      return;
    }
    if (!el) {
      el = document.createElement('div');
      el.className = CLASSES.root;
      el.setAttribute('data-fc-code-chrome', '');
      el.innerHTML = `
        <button type="button" class="${CLASSES.copy}" data-fc-action="copy" aria-label="Copy code" title="Copy code">
          <span class="fc-code-copy-label">Copy</span>
        </button>
        <button type="button" class="${CLASSES.menu}" data-fc-action="menu" aria-label="Code block menu" aria-haspopup="true" aria-expanded="false" title="Menu">
          <span aria-hidden="true">⋯</span>
        </button>
        <div class="${CLASSES.scrim}" data-fc-menu="panel" hidden></div>`;
      host.appendChild(el);
      el.addEventListener('click', onClick);
    }
    el.dataset.language = active.language;
    el.setAttribute('data-fc-copy-text', active.text);
    const panel = el.querySelector<HTMLElement>('[data-fc-menu="panel"]')!;
    const open = el.classList.contains(CLASSES.menuOpen);
    panel.hidden = !open;
    el.querySelector(`.${CLASSES.menu}`)!.setAttribute('aria-expanded', String(open));
    // Rebuild the panel ONLY when its content would change.
    //
    // This used to be unconditional on every animation frame, so while the
    // menu was open the buttons were destroyed and recreated ~60 times a
    // second. A click resolved against one instance and dispatched against
    // a detached node, and nothing happened — measured: taking a
    // screenshot between opening the menu and clicking "python" (which
    // forces a paint) made change-language fail in production while the
    // identical click succeeded when nothing forced a frame in between.
    if (open && panel.dataset.fcLang !== active.language) {
      panel.innerHTML = menuMarkup(active.language);
      panel.dataset.fcLang = active.language;
    }
  };

  const schedule = () => {
    if (raf) return;
    raf = requestAnimationFrame(render);
  };

  const setActive = (next: CodeChromeRefs['active']) => {
    // Same-ness is element identity now, not id equality: two blocks can
    // carry the same id, and moving between them must re-render.
    const same = (!active && !next) || (active && next && active.el === next.el);
    active = next;
    if (!same) host.classList.toggle('fc-code-chrome-on', !!next);
    schedule();
  };

  const onMove = (ev: PointerEvent) => {
    // The chrome is a child of the HOST, not of the code block, so moving
    // the pointer onto the copy button looks exactly like leaving the
    // block — and the button deletes itself under the cursor mid-click.
    // Ignore anything that originated inside the chrome.
    if (el && (ev.target as HTMLElement)?.closest?.('[data-fc-code-chrome]') === el) return;
    const target = (ev.target as HTMLElement)?.closest?.('[data-content-type="codeBlock"]') as HTMLElement | null;
    if (!target) { setActive(null); return; }
    const info = readBlock(target);
    setActive(info);
  };
  const onLeave = (ev: PointerEvent) => {
    // Same reason as onMove: leaving toward the chrome is not leaving.
    if (el && (ev.target as HTMLElement)?.closest?.('[data-fc-code-chrome]') === el) return;
    setActive(null);
  };

  const onFocus = (ev: FocusEvent) => {
    const target = (ev.target as HTMLElement)?.closest?.('[data-content-type="codeBlock"]') as HTMLElement | null;
    if (!target) return;
    const info = readBlock(target);
    setActive(info);
  };

  const onClick = async (ev: Event) => {
    const btn = (ev.target as HTMLElement)?.closest?.('[data-fc-action],[data-fc-menu-action]') as HTMLElement | null;
    if (!btn) return;
    ev.preventDefault();
    ev.stopPropagation();
    const action = btn.getAttribute('data-fc-action') ?? btn.getAttribute('data-fc-menu-action');
    if (action === 'copy') {
      await doCopy(btn);
      return;
    }
    if (action === 'menu') {
      el?.classList.toggle(CLASSES.menuOpen);
      schedule();
      return;
    }
    if (action === 'close') {
      el?.classList.remove(CLASSES.menuOpen);
      schedule();
      return;
    }
    if (action === 'duplicate') return void duplicate(view, active?.el);
    if (action === 'delete') return void remove(view, active?.el);
    if (action === 'lang') {
      const v = btn.getAttribute('data-fc-language');
      if (v) { setLanguage(view, active?.el, v); el?.classList.remove(CLASSES.menuOpen); schedule(); }
      return;
    }
  };

  host.addEventListener('pointermove', onMove, { passive: true });
  host.addEventListener('pointerleave', onLeave);
  view.dom.addEventListener('focusin', onFocus);
  render();

  return {
    getActive: () => active,
    refresh: () => {
      if (!active) return;
      const i = readBlock(active.el);
      if (i) active = i;
      schedule();
    },
    destroy,
  };
}

async function doCopy(btn: HTMLElement) {
  const label = btn.querySelector<HTMLElement>('.fc-code-copy-label');
  // The chrome element carries the text of the block it is attached to.
  // Reading it from the DOM (not from a closure over editor state) keeps
  // the handler correct even if the pointer moved between render and click.
  const text = btn.closest<HTMLElement>('[data-fc-code-chrome]')?.getAttribute('data-fc-copy-text') ?? '';
  let ok = false;
  try {
    await navigator.clipboard.writeText(text);
    ok = true;
  } catch {
    // clipboard API is unavailable over plain http or without permission
    ok = legacyCopy(text);
  }
  const root = btn.closest<HTMLElement>('[data-fc-code-chrome]');
  if (ok) {
    btn.classList.add(CLASSES.copied);
    if (label) label.textContent = 'Copied';
    root?.setAttribute('data-fc-copied', 'true');
  } else {
    root?.setAttribute('data-fc-copied', 'false');
  }
  setTimeout(() => {
    btn.classList.remove(CLASSES.copied);
    if (label) label.textContent = 'Copy';
    root?.removeAttribute('data-fc-copied');
  }, 1000);
}

function legacyCopy(text: string): boolean {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch { return false; }
}

/**
 * Position of a code block, resolved from its DOM node.
 *
 * This deliberately does NOT match on an id. Two id schemes exist —
 * the DOM's `data-id` and the ProseMirror node's `attrs.id` — and a
 * React-fiber probe of a rendered page found neither reliably, so every
 * action that keyed off one silently did nothing: duplicate, delete and
 * change-language all reported success and changed nothing. `posAtDOM`
 * needs no id at all and stays correct after the document is edited,
 * because it re-resolves at the moment the action runs.
 */
function posOf(view: EditorView, el?: HTMLElement | null): number {
  if (!el || !el.isConnected) return -1;
  try {
    const pos = view.posAtDOM(el, 0);
    // posAtDOM on a block's inner node can land INSIDE it; walk up to the
    // position that precedes the block itself.
    const $pos = view.state.doc.resolve(pos);
    for (let d = $pos.depth; d > 0; d--) {
      if ($pos.node(d).type.name === 'codeBlock') return $pos.before(d);
    }
    return -1;
  } catch {
    return -1;
  }
}

function duplicate(view: EditorView, el?: HTMLElement | null) {
  const pos = posOf(view, el);
  if (pos < 0) return;
  const node = view.state.doc.nodeAt(pos);
  if (!node) return;
  const tr = view.state.tr.insert(pos + node.nodeSize, node.copy(node.content));
  view.dispatch(tr);
  view.focus();
}

function remove(view: EditorView, el?: HTMLElement | null) {
  const pos = posOf(view, el);
  if (pos < 0) return;
  const node = view.state.doc.nodeAt(pos);
  if (!node) return;
  view.dispatch(view.state.tr.delete(pos, pos + node.nodeSize));
  view.focus();
}

function setLanguage(view: EditorView, el: HTMLElement | null | undefined, language: string) {
  const pos = posOf(view, el);
  if (pos < 0) return;
  const node = view.state.doc.nodeAt(pos);
  if (!node) return;
  view.dispatch(view.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, language }));
  view.focus();
}

export { LANGUAGES, DEFAULT_LANGUAGE, setLanguage, duplicate, remove };
